/**
 * Incoming SMS API Schemas
 *
 * SMS Audit Trail - Admin API
 *
 * Admin endpoints for viewing and analyzing incoming SMS messages.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../../common/response.schema";

/**
 * Query parameters for listing incoming SMS
 */
export const ListIncomingSmsParamsSchema = z.object({
  phoneNumberId: z.string().uuid().optional(),
  organizationId: z.string().optional(),
  from: z.string().optional(),
  startDate: z.string().datetime().optional(), // ISO datetime
  endDate: z.string().datetime().optional(), // ISO datetime
  wasWhatsappVerificationOtp: z.coerce.boolean().optional(),
  status: z.string().optional(), // Filter by status: success, pending, no_deployment, workflow_error, etc.
  limit: z.coerce.number().int().min(1).max(1000).optional().default(50),
  offset: z.coerce.number().int().min(0).optional().default(0)
});

/**
 * Single incoming SMS response
 */
export const IncomingSmsSchema = z.object({
  id: z.string().uuid(),
  messageSid: z.string(),
  accountSid: z.string(),
  from: z.string(),
  to: z.string(),
  body: z.string(),
  numMedia: z.number().int(),
  mediaUrls: z.array(z.string()),
  mediaContentTypes: z.array(z.string()),
  smsStatus: z.string().nullable(),
  messageStatus: z.string().nullable(),
  direction: z.literal("inbound"),
  receivedAt: z.coerce.date(),
  twilioTimestamp: z.coerce.date().nullable(),
  phoneNumberId: z.string().uuid().nullable(),
  organizationId: z.string().nullable(),
  apiKeyConnectionId: z.string().uuid().nullable(),
  status: z.string(), // success, pending, no_deployment, workflow_error, etc.
  processedByDeploymentId: z.string().uuid().nullable(),
  processedAt: z.coerce.date().nullable(),
  processingError: z.string().nullable(),
  wasWhatsappVerificationOtp: z.boolean(),
  whatsappSenderId: z.string().nullable()
});

/**
 * List incoming SMS response
 */
export const ListIncomingSmsResponseSchema = z.object({
  total: z.number().int(),
  messages: z.array(IncomingSmsSchema),
  hasMore: z.boolean()
});

/**
 * Incoming SMS API Endpoints
 */
export const ZIncomingSms = {
  List: {
    method: "GET" as const,
    path: "/api/admin/incoming-sms",
    Params: ListIncomingSmsParamsSchema,
    Response: ApiSuccessSchema(ListIncomingSmsResponseSchema)
  }
} as const;
